/**
 * Invoice Template Service
 *
 * Provides CRUD operations for invoice templates.
 * All functions call the backend API endpoints.
 */

import type { InvoiceTemplate, TemplateSection, TemplateBranding, TemplateSettings } from '@/lib/invoice-template-types';
import { DEFAULT_BRANDING, DEFAULT_SETTINGS } from '@/lib/invoice-template-types';
import { getApiBaseUrl } from '@/lib/data-service';
import { apiFetch } from '@/lib/api-error-handler';

/**
 * The backend stores the rich template config (sections, branding, settings)
 * inside a single NVARCHAR(MAX) column called TemplateJson.
 * These helpers convert between the flat frontend InvoiceTemplate shape and
 * the backend shape where those fields live inside templateJson.
 */

interface ApiInvoiceTemplate {
  id: number;
  name: string;
  description?: string;
  isDefault: boolean;
  templateJson: string;
  createdBy?: string;
  updatedBy?: string;
  organisationId?: string;
  companyId?: string;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Normalises a template logo URL to the query-parameter proxy format.
 * Handles all legacy URL shapes that may be stored in templateJson.
 */
function normaliseLogoUrl(logo: string | undefined): string | undefined {
  if (!logo) return logo;

  // Already in the new query-param format
  if (logo.includes('/invoice-templates/logo-image?path=')) return logo;

  // Direct Azure Blob Storage URL
  const blobMatch = logo.match(/https?:\/\/[^/]+\.blob\.core\.windows\.net\/template-logos\/(.+)/);
  if (blobMatch) {
    return `${getApiBaseUrl()}/invoice-templates/logo-image?path=${encodeURIComponent(blobMatch[1])}`;
  }

  // Old path-based proxy URL: .../invoice-templates/logo/{blobPath}
  const proxyMatch = logo.match(/\/invoice-templates\/logo\/(.+)/);
  if (proxyMatch) {
    return `${getApiBaseUrl()}/invoice-templates/logo-image?path=${encodeURIComponent(proxyMatch[1])}`;
  }

  return logo;
}

function fromApi(api: ApiInvoiceTemplate): InvoiceTemplate {
  let parsed: { sections?: TemplateSection[]; branding?: TemplateBranding; settings?: TemplateSettings } = {};
  try {
    parsed = api.templateJson ? JSON.parse(api.templateJson) : {};
  } catch {
    // malformed JSON — use defaults
  }
  const branding = parsed.branding ?? DEFAULT_BRANDING;
  if (branding.logo) {
    branding.logo = normaliseLogoUrl(branding.logo) ?? branding.logo;
  }
  return {
    id: api.id,
    name: api.name,
    description: api.description ?? '',
    isDefault: api.isDefault,
    sections: parsed.sections ?? [],
    branding,
    settings: parsed.settings ?? DEFAULT_SETTINGS,
    createdAt: api.createdAt ? new Date(api.createdAt) : new Date(),
    updatedAt: api.updatedAt ? new Date(api.updatedAt) : new Date(),
    createdBy: api.createdBy,
  };
}

function toApi(template: Omit<InvoiceTemplate, 'id'> & { id?: number }): Record<string, unknown> {
  const { sections, branding, settings, createdAt, updatedAt, ...rest } = template;
  return {
    ...rest,
    templateJson: JSON.stringify({ sections, branding, settings }),
  };
}

// ============================================================================
// READ
// ============================================================================

/**
 * Returns all invoice templates available to the current company.
 *
 * API: GET /api/invoice-templates
 */
export async function getInvoiceTemplates(): Promise<InvoiceTemplate[]> {
  const data = await apiFetch<ApiInvoiceTemplate[]>(`${getApiBaseUrl()}/invoice-templates`);
  return data.map(fromApi);
}

/**
 * Returns a single invoice template by ID.
 *
 * API: GET /api/invoice-templates/{id}
 */
export async function getInvoiceTemplate(
  id: string | number,
): Promise<InvoiceTemplate | undefined> {
  try {
    const data = await apiFetch<ApiInvoiceTemplate>(`${getApiBaseUrl()}/invoice-templates/${id}`);
    return fromApi(data);
  } catch (err: unknown) {
    if (err && typeof err === 'object' && 'metadata' in err) {
      const meta = err as { metadata?: { statusText?: string } };
      if (meta.metadata?.statusText === 'Not Found') return undefined;
    }
    throw err;
  }
}

// ============================================================================
// WRITE
// ============================================================================

/**
 * Creates a new invoice template.
 *
 * API: POST /api/invoice-templates
 */
export async function createInvoiceTemplate(
  template: Omit<InvoiceTemplate, 'id'>,
): Promise<InvoiceTemplate> {
  const data = await apiFetch<ApiInvoiceTemplate>(`${getApiBaseUrl()}/invoice-templates`, {
    method: 'POST',
    body: JSON.stringify(toApi(template)),
  });
  return fromApi(data);
}

/**
 * Updates an existing invoice template.
 *
 * API: PUT /api/invoice-templates/{id}
 */
export async function updateInvoiceTemplate(
  template: InvoiceTemplate,
): Promise<InvoiceTemplate> {
  await apiFetch<string>(`${getApiBaseUrl()}/invoice-templates/${template.id}`, {
    method: 'PUT',
    body: JSON.stringify(toApi(template)),
  });
  // PUT returns 204 No Content, return the template with updated timestamp
  return { ...template, updatedAt: new Date() };
}

/**
 * Deletes an invoice template by ID.
 *
 * API: DELETE /api/invoice-templates/{id}
 */
export async function deleteInvoiceTemplate(id: string | number): Promise<void> {
  await apiFetch<string>(`${getApiBaseUrl()}/invoice-templates/${id}`, {
    method: 'DELETE',
  });
}

// ============================================================================
// LOGO UPLOAD
// ============================================================================

/**
 * Uploads a logo image (base64 data URL) to blob storage and returns the
 * proxied URL to display the logo.
 *
 * API: POST /api/invoice-templates/logo
 * Returns: { logoUrl: "<blobName>" }
 * Display via: GET /api/invoice-templates/logo-image?path={blobName}
 */
export async function uploadTemplateLogo(base64Data: string): Promise<string> {
  const response = await apiFetch<{ logoUrl: string }>(`${getApiBaseUrl()}/invoice-templates/logo`, {
    method: 'POST',
    body: JSON.stringify({ base64Data }),
  });
  return `${getApiBaseUrl()}/invoice-templates/logo-image?path=${encodeURIComponent(response.logoUrl)}`;
}
